import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Modal component that displays a cancellation error and provides controls to acknowledge or dismiss it.
 * @component
 * @selector app-cancellation-error-modal
 * @example
 *   <app-cancellation-error-modal [isVisible]="true" (okay)="handleOkay()" (close)="handleClose()"></app-cancellation-error-modal>
 * @input {boolean} isVisible - Controls the visibility of the modal; when true the modal is shown.
 * @output {EventEmitter<void>} okay - Emitted when the user acknowledges the error (e.g., clicks an OK/confirm button).
 * @output {EventEmitter<void>} close - Emitted when the modal is dismissed or closed (e.g., via a close button or backdrop).
 *
 * Purpose:
 *   Presents a standardized cancellation-error message and surface actions for the parent to respond to user interaction.
 *
 * Key methods:
 *   - onOkay(): void — Emits the 'okay' event to notify parent components that the user confirmed/acknowledged the error.
 *   - onClose(): void — Emits the 'close' event to notify parent components that the modal was dismissed.
 *
 * Lifecycle:
 *   - This is a simple presentational component and does not implement any Angular lifecycle hooks by default.
 */
@Component({
  selector: 'app-cancellation-error-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cancellation-error-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cancellation-error-modal.component.scss',
})
export class CancellationErrorModalComponent {
  @Input() isVisible = false;
  @Output() okay = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  onOkay(): void {
    this.okay.emit();
  }

  onClose(): void {
    this.close.emit();
  }
}
